import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Button, Stack, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { SelectElement, TextFieldElement } from 'react-hook-form-mui';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { geographicBoundaryTypeCountryId } from '../constants';
import { useGeographicBoundary } from '../hooks/api/geographic-boundary';
import { useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import { AutocompleteDialog } from './AutocompleteDialog';
import { IGeographicBoundaryResult } from '../utils/types';

const individualFormSchema = z.object({
  id: z.number().optional(),
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  birthDate: z.date(),
  birthCountry: z.number(),
  gender: z.enum(['MALE', 'FEMALE']),
  maritalStatus: z.enum(['SINGLE', 'MARRIED', 'NON_SPECIFIED']),
  citizenshipNumber: z.string().min(2),
  citizenshipCountry: z.number(),
});

export type IndividualFormValues = z.infer<typeof individualFormSchema>;

export function IndividualForm(props: {
  initialValues?: IndividualFormValues;
  birthDateMin?: Date;
  birthDateMax?: Date;
  onSubmit: (data: IndividualFormValues) => void;
  submitting?: boolean;
}) {
  const { data: countries } = useGeographicBoundary({
    typeId: geographicBoundaryTypeCountryId,
  });
  let schema = individualFormSchema;
  if (props.birthDateMin && props.birthDateMax) {
    schema = individualFormSchema.extend({
      birthDate: z.date().min(props.birthDateMin).max(props.birthDateMax),
    });
  }
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: props.initialValues
      ? {
          ...props.initialValues,
          birthDate: new Date(props.initialValues.birthDate),
        }
      : {
          firstName: '',
          lastName: '',
          citizenshipNumber: '',
          birthCountry: 18884,
          citizenshipCountry: 18884,
        },
  });

  useEffect(() => {
    if (props.initialValues?.id) form.setValue('id', props.initialValues.id);
  }, [form, props.initialValues]);

  const onSubmit = form.handleSubmit((data) => {
    props.onSubmit(data as IndividualFormValues);
  });
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Controller
          name="citizenshipCountry"
          control={form.control}
          render={({ field }) => (
            <AutocompleteDialog
              title={t('individualForm.citizenshipCountry')}
              options={countries || []}
              getOptionLabel={(option) =>
                (option as IGeographicBoundaryResult).name
              }
              renderInput={(params) => (
                <TextField
                  label={t('individualForm.citizenshipCountry')}
                  name="citizenshipCountry"
                  {...params}
                />
              )}
              value={countries?.find((c) => c.id === field.value) || null}
              onChange={(_, value) => {
                field.onChange((value as IGeographicBoundaryResult)?.id || 0);
              }}
              renderTrigger={(props) => (
                <TextField
                  label={t('individualForm.citizenshipCountry')}
                  {...props}
                  value={
                    countries?.find((c) => c.id === field.value)?.name || ''
                  }
                  error={!!form.formState.errors.citizenshipCountry?.message}
                  helperText={
                    form.formState.errors.citizenshipCountry?.message &&
                    t(form.formState.errors.citizenshipCountry?.message)
                  }
                />
              )}
            />
          )}
        />
        <TextFieldElement
          label={t('individualForm.citizenshipNumber')}
          name="citizenshipNumber"
          control={form.control}
          inputProps={{
            inputMode: 'numeric',
          }}
        />

        <TextFieldElement
          label={t('individualForm.firstName')}
          name="firstName"
          control={form.control}
        />
        <TextFieldElement
          label={t('individualForm.lastName')}
          name="lastName"
          control={form.control}
        />
        <SelectElement
          label={t('individualForm.birthCountry')}
          name="birthCountry"
          control={form.control}
          options={[{ label: 'Turkey', id: 18884 }]}
        />
        <SelectElement
          label={t('individualForm.gender')}
          name="gender"
          control={form.control}
          options={[
            { label: t('individual.female'), id: 'FEMALE' },
            { label: t('individualForm.male'), id: 'MALE' },
          ]}
        />
        <DatePickerElement
          label={t('individualForm.birthDate')}
          name="birthDate"
          control={form.control}
        />
        <SelectElement
          label={t('individualForm.maritalStatus')}
          name="maritalStatus"
          control={form.control}
          options={[
            { label: t('individualForm.single'), id: 'SINGLE' },
            { label: t('individualForm.married'), id: 'MARRIED' },
            { label: t('individualForm.nonSpecified'), id: 'NON_SPECIFIED' },
          ]}
        />

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            variant="text"
            onClick={() =>
              form.reset({
                firstName: '',
                lastName: '',
                citizenshipNumber: '',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                birthCountry: null as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                citizenshipCountry: null as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                gender: null as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                maritalStatus: null as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                birthDate: null as any,
              })
            }
            color="inherit"
          >
            {t('individualForm.reset')}
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={props.submitting}
            sx={{ minWidth: '150px' }}
          >
            {t('individualForm.save')}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
}
